import React, { useState } from 'react';
import { Dropdown } from 'antd';
import {
  DateTimeSetExpr,
  RecurringWindowExpr,
  VariableValue,
} from '@/proto/logiblocks/logiblocks';
import { CustomSelectIcon } from '@/assets/icons/icons';
import { getUserTimeZone } from '@/common/utils/utils';
import { SavedDateList, SystemDatePresetSummary } from '@/apis/logiblocksAPI';
import DateSetValueMenu from './DateSetValueMenu';
import LogiblocksDatePickerPanel from './LogiblocksDatePickerPanel';
import SaveListPanel from './SaveListPanel';
import ListEditPanel from './ListEditPanel';
import { useSavedDateLists, useSystemDatePresets } from './dateSetQueries';
import { DateSetValue } from './dateSetTypes';
import {
  buildCustomDateTimeSetExpr,
  buildInitialCustomValue,
  customSetName,
  DateSetFieldSource,
  getTriggerLabel,
} from './dateSetUtils';
import styles from './DateTimeConditionValueField.module.css';

type Screen =
  | { name: 'menu' }
  | { name: 'calendar'; value: DateSetValue }
  | { name: 'saveList'; value: DateSetValue }
  | { name: 'editList'; listId: string };

interface DateTimeConditionValueFieldProps {
  dateTimeSetExpr: DateTimeSetExpr;
  onChange: (next: DateTimeSetExpr) => void;
  /** The legacy per-day-of-week shape, read only to display and seed the
   * picker for a condition that hasn't been re-edited. */
  legacyRecurringWindowExpr?: RecurringWindowExpr;
  /** The oldest single-literal shape, read only for the trigger label. */
  legacyLiteralValue?: VariableValue;
  disabled?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
}

/** The Within / Not Within value field: a dropdown trigger showing the
 * current preset, saved list or custom selection, whose panel switches
 * between the value menu, the calendar picker, and the save-list and
 * list-edit panels. */
const DateTimeConditionValueField: React.FC<
  DateTimeConditionValueFieldProps
> = ({
  dateTimeSetExpr,
  onChange,
  legacyRecurringWindowExpr,
  legacyLiteralValue,
  disabled = false,
  placeholder = 'Select...',
  style,
}) => {
  const [open, setOpen] = useState(false);
  const [screen, setScreen] = useState<Screen>({ name: 'menu' });
  const presetsQuery = useSystemDatePresets();
  const listsQuery = useSavedDateLists();

  const source: DateSetFieldSource = {
    expr: dateTimeSetExpr,
    legacyRecurringWindowExpr,
    legacyLiteralValue,
  };
  const triggerLabel = getTriggerLabel(
    source,
    presetsQuery.data?.presets,
    listsQuery.data
  );

  const commit = (next: DateTimeSetExpr) => {
    onChange(next);
    setOpen(false);
    setScreen({ name: 'menu' });
  };

  const handleSelectPreset = (preset: SystemDatePresetSummary) => {
    commit({
      variable: dateTimeSetExpr.variable,
      iana_timezone:
        preset.iana_timezone ||
        dateTimeSetExpr.iana_timezone ||
        getUserTimeZone().name,
      operator: dateTimeSetExpr.operator,
      display_label: preset.display_name,
      system_preset_id: preset.preset_id,
    });
  };

  const handleSelectSavedList = (list: SavedDateList) => {
    commit({
      variable: dateTimeSetExpr.variable,
      iana_timezone: list.iana_timezone,
      operator: dateTimeSetExpr.operator,
      display_label: list.name,
      saved_list_id: list.id,
    });
  };

  const handleCalendarSubmit = (value: DateSetValue) => {
    commit(
      buildCustomDateTimeSetExpr(
        dateTimeSetExpr.variable,
        value,
        customSetName(value.entries.kind)
      )
    );
  };

  const renderScreen = (): React.ReactNode => {
    switch (screen.name) {
      case 'menu':
        return (
          <DateSetValueMenu
            presets={presetsQuery.data?.presets ?? []}
            savedLists={listsQuery.data ?? []}
            loading={presetsQuery.isLoading || listsQuery.isLoading}
            loadFailed={presetsQuery.isError || listsQuery.isError}
            onRetry={() =>
              Promise.all([presetsQuery.refetch(), listsQuery.refetch()])
            }
            onSelectPreset={handleSelectPreset}
            onSelectSavedList={handleSelectSavedList}
            onEditSavedList={(list) =>
              setScreen({ name: 'editList', listId: list.id })
            }
            onSelectCustomDates={() =>
              setScreen({
                name: 'calendar',
                value: buildInitialCustomValue(source),
              })
            }
          />
        );
      case 'calendar':
        return (
          <LogiblocksDatePickerPanel
            value={screen.value}
            footerVerb='Submit'
            onSubmit={handleCalendarSubmit}
            onSaveAsList={(value) => setScreen({ name: 'saveList', value })}
            disabled={disabled}
          />
        );
      case 'saveList':
        return (
          <SaveListPanel
            value={screen.value}
            onValueChange={(value) => setScreen({ name: 'saveList', value })}
            onBack={() => setScreen({ name: 'calendar', value: screen.value })}
            onSaved={handleSelectSavedList}
          />
        );
      case 'editList':
        return (
          <ListEditPanel
            listId={screen.listId}
            onBack={() => setScreen({ name: 'menu' })}
            onUpdated={(list) => {
              // Keep the denormalized label of a condition using this list in step with a rename.
              if (dateTimeSetExpr.saved_list_id === list.id) {
                onChange({ ...dateTimeSetExpr, display_label: list.name });
              }
              setScreen({ name: 'menu' });
            }}
            onDeleted={() => setScreen({ name: 'menu' })}
          />
        );
      default: {
        const unhandled: never = screen;
        return unhandled;
      }
    }
  };

  return (
    <Dropdown
      open={open && !disabled}
      onOpenChange={(flag) => {
        if (disabled) return;
        setOpen(flag);
        if (!flag) setScreen({ name: 'menu' });
      }}
      dropdownRender={() => <div>{renderScreen()}</div>}
      trigger={['click']}
      placement='bottomLeft'
    >
      <button
        type='button'
        className={`${styles.trigger} ${triggerLabel ? '' : styles.placeholder}`}
        style={style}
        disabled={disabled}
        aria-haspopup='menu'
        aria-expanded={open && !disabled}
      >
        <span>{triggerLabel ?? placeholder}</span>
        <CustomSelectIcon />
      </button>
    </Dropdown>
  );
};

export default DateTimeConditionValueField;
